if (!customElements.get('authors-directory')) {
  class AuthorsDirectory extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('[data-authors-search-form]');
      this.input = this.querySelector('[data-authors-search-input]');
      this.status = this.querySelector('[data-authors-status]');
      this.results = this.querySelector('[data-authors-results]');
      this.resultsList = this.querySelector('[data-authors-results-list]');
      this.catalogue = this.querySelector('[data-authors-catalogue]');
      this.alphabet = this.querySelector('[data-authors-alphabet]');
      this.pageStatus = this.status ? this.status.textContent.trim() : '';
      this.catalogueInitiallyHidden = this.catalogue ? this.catalogue.hidden : true;
      this.locale = this.dataset.locale || document.documentElement.lang || 'en';
      this.pageCount = Number(this.dataset.pageCount) || 1;
      this.totalCount = Number(this.dataset.totalCount) || 0;
      this.resultLimit = 500;
      this.entries = null;
      this.loadPromise = null;
      this.loadFailed = false;
      this.searchSequence = 0;
      this.inputTimer = null;
      this.collator = new Intl.Collator(this.locale, { sensitivity: 'base', numeric: true });

      if (!this.form || !this.input || !this.status || !this.results || !this.resultsList || !this.catalogue) return;

      this.handleFocusBound = this.handleFocus.bind(this);
      this.handleInputBound = this.handleInput.bind(this);
      this.handleSubmitBound = this.handleSubmit.bind(this);
      this.handleResetBound = this.handleReset.bind(this);

      this.input.addEventListener('focus', this.handleFocusBound, { once: true });
      this.input.addEventListener('input', this.handleInputBound);
      this.form.addEventListener('submit', this.handleSubmitBound);
      this.form.addEventListener('reset', this.handleResetBound);
    }

    disconnectedCallback() {
      if (!this.input || !this.form) return;

      window.clearTimeout(this.inputTimer);
      this.input.removeEventListener('focus', this.handleFocusBound);
      this.input.removeEventListener('input', this.handleInputBound);
      this.form.removeEventListener('submit', this.handleSubmitBound);
      this.form.removeEventListener('reset', this.handleResetBound);
    }

    handleFocus() {
      this.prepareIndex();
    }

    handleInput() {
      window.clearTimeout(this.inputTimer);
      this.searchSequence += 1;
      const hasQuery = this.input.value.trim().length > 0;
      this.form.classList.toggle('has-active-filter', hasQuery);

      if (!hasQuery) {
        this.resetSearch();
        return;
      }

      this.setTextSearchMode(true);
      this.inputTimer = window.setTimeout(() => this.runSearch(), 120);
    }

    handleSubmit(event) {
      event.preventDefault();
      window.clearTimeout(this.inputTimer);

      if (this.input.value.trim()) {
        this.setTextSearchMode(true);
        this.runSearch();
      } else {
        this.resetSearch();
      }
    }

    handleReset() {
      window.clearTimeout(this.inputTimer);
      this.searchSequence += 1;
      window.setTimeout(() => this.resetSearch(), 0);
    }

    async runSearch() {
      const query = this.input.value.trim();
      if (!query) {
        this.resetSearch();
        return;
      }

      this.setTextSearchMode(true);
      const sequence = ++this.searchSequence;
      await this.prepareIndex();
      if (sequence !== this.searchSequence) return;
      if (!this.entries) {
        if (this.loadFailed) this.setTextSearchMode(false);
        return;
      }

      const normalizedQuery = this.normalizeText(query);
      const tokens = normalizedQuery.split(' ').filter(Boolean);
      const matches = this.entries.filter((entry) => tokens.every((token) => entry.searchText.includes(token)));
      this.renderResults(matches, query);
    }

    async prepareIndex() {
      if (this.entries) return this.entries;
      if (this.loadPromise) return this.loadPromise;

      this.setAttribute('aria-busy', 'true');
      this.setStatus(this.dataset.messageLoading);

      this.loadPromise = this.loadIndex()
        .then((entries) => {
          this.entries = entries.map((entry) => this.prepareEntry(entry)).sort((a, b) => (
            this.collator.compare(a.sortName || a.name, b.sortName || b.name)
          ));
          this.loadFailed = false;
          this.removeAttribute('aria-busy');

          if (!this.input.value.trim()) this.setStatus(this.dataset.messageReady);
          return this.entries;
        })
        .catch((error) => {
          this.loadFailed = true;
          this.loadPromise = null;
          this.removeAttribute('aria-busy');
          this.setStatus(this.dataset.messageError);
          console.warn('Unable to load the complete authors index.', error); // eslint-disable-line no-console
          return null;
        });

      return this.loadPromise;
    }

    async loadIndex() {
      const cachedEntries = this.readCache();
      if (cachedEntries) return cachedEntries;

      const sectionId = this.dataset.searchIndexSectionId;
      if (!sectionId) throw new Error('Authors search index section ID is missing.');

      const firstPage = await this.fetchIndexPage(1, sectionId, 'page');
      const pageCount = Number(firstPage.pageCount) || this.pageCount;
      const pageParam = firstPage.pageParam || 'page';
      const batches = [firstPage.entries];
      const pages = [];
      for (let page = 2; page <= pageCount; page += 1) {
        pages.push(page);
      }

      let cursor = 0;
      const worker = async () => {
        while (cursor < pages.length) {
          const page = pages[cursor];
          cursor += 1;
          const indexPage = await this.fetchIndexPage(page, sectionId, pageParam);
          batches.push(indexPage.entries);
        }
      };
      const workers = Array.from({ length: Math.min(3, pages.length) }, () => worker());
      await Promise.all(workers);

      const entriesById = new Map();
      batches.flat().forEach((entry) => {
        if (!entry || !entry.name || !entry.url) return;
        const key = entry.id || `${entry.name}|${entry.url}`;
        entriesById.set(String(key), entry);
      });

      const entries = Array.from(entriesById.values());
      if (entries.length !== this.totalCount) {
        throw new Error(`Incomplete authors index: expected ${this.totalCount}, received ${entries.length}.`);
      }

      this.writeCache(entries);
      return entries;
    }

    async fetchIndexPage(page, sectionId, pageParam) {
      const url = new URL(window.location.pathname, window.location.origin);
      url.searchParams.set(pageParam, String(page));
      url.searchParams.set('section_id', sectionId);

      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(`Authors index request failed with ${response.status}.`);

      const template = document.createElement('template');
      template.innerHTML = await response.text();
      const indexSection = template.content.querySelector('[data-authors-search-index]');
      if (!indexSection) throw new Error(`Authors index page ${page} returned no data.`);
      return {
        entries: this.parseIndex(indexSection),
        pageCount: indexSection.dataset.pageCount,
        pageParam: indexSection.dataset.pageParam
      };
    }

    parseIndex(indexSection) {
      const data = indexSection.querySelector('[data-authors-search-data]');
      if (!data) throw new Error('Authors search data was not found.');

      const entries = JSON.parse(data.textContent);
      if (!Array.isArray(entries)) throw new Error('Authors search data is invalid.');
      return entries;
    }

    prepareEntry(entry) {
      const aliases = [
        ...this.toStringArray(entry.aliases),
        ...this.toStringArray(entry.searchAliases),
        ...this.toStringArray(entry.alternateNames)
      ];
      const sortName = entry.sortName || entry.name;

      return {
        ...entry,
        sortName,
        initials: this.getInitials(entry.name),
        searchText: this.normalizeText([entry.name, sortName, ...aliases].join(' '))
      };
    }

    toStringArray(value) {
      if (Array.isArray(value)) return value.flat().filter(Boolean).map(String);
      if (typeof value !== 'string' || !value.trim()) return [];
      return value.split(/[;|\n]+/).map((part) => part.trim()).filter(Boolean);
    }

    normalizeText(value) {
      return String(value || '')
        .normalize('NFKD')
        .replace(/[\u0300-\u036f\u1ab0-\u1aff\u1dc0-\u1dff\u20d0-\u20ff\uFE20-\uFE2F]/g, '')
        .toLocaleLowerCase(this.locale)
        .replace(/ё/g, 'е')
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .trim();
    }

    toLocaleUpper(value) {
      return String(value || '').toLocaleUpperCase(this.locale);
    }

    getInitials(value) {
      const words = String(value || '').trim().split(/\s+/).filter(Boolean);
      if (words.length === 0) return '';

      const first = Array.from(words[0])[0] || '';
      const last = words.length > 1 ? Array.from(words[words.length - 1])[0] || '' : '';
      return this.toLocaleUpper(`${first}${last}`);
    }

    renderResults(matches, query) {
      const visibleMatches = matches.slice(0, this.resultLimit);
      const fragment = document.createDocumentFragment();

      visibleMatches.forEach((entry) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        const avatar = document.createElement('span');
        const name = document.createElement('span');
        item.className = 'authors-directory__item';
        link.className = 'authors-directory__link';
        avatar.className = 'authors-directory__avatar';
        avatar.setAttribute('aria-hidden', 'true');
        name.className = 'authors-directory__name';
        link.href = entry.url;
        name.textContent = entry.name;

        if (entry.photoUrl) {
          const image = document.createElement('img');
          image.className = 'authors-directory__avatar-image';
          image.src = entry.photoUrl;
          image.alt = '';
          image.width = 40;
          image.height = 40;
          image.loading = 'lazy';
          image.decoding = 'async';
          avatar.appendChild(image);
        } else {
          const initials = document.createElement('span');
          initials.className = 'authors-directory__avatar-initials';
          initials.textContent = entry.initials;
          avatar.appendChild(initials);
        }

        link.append(avatar, name);
        item.appendChild(link);
        fragment.appendChild(item);
      });

      this.resultsList.replaceChildren(fragment);
      this.catalogue.hidden = true;
      this.results.hidden = false;

      if (matches.length === 0) {
        this.setStatus(this.formatMessage(this.dataset.messageNoResults, { __QUERY__: query }));
      } else if (matches.length > visibleMatches.length) {
        this.setStatus(this.formatMessage(this.dataset.messageResultsTruncated, {
          __COUNT__: matches.length,
          __VISIBLE__: visibleMatches.length
        }));
      } else {
        this.setStatus(this.formatMessage(this.dataset.messageResults, { __COUNT__: matches.length }));
      }
    }

    resetSearch() {
      this.searchSequence += 1;
      this.form.classList.remove('has-active-filter');
      this.setTextSearchMode(false);
      this.resultsList.replaceChildren();
      this.setStatus(this.pageStatus);
    }

    setTextSearchMode(isActive) {
      if (this.alphabet) this.alphabet.hidden = isActive;
      this.catalogue.hidden = isActive ? true : this.catalogueInitiallyHidden;
      this.results.hidden = !isActive;

      if (isActive) this.resultsList.replaceChildren();
    }

    setStatus(message) {
      this.status.textContent = message || '';
    }

    formatMessage(template, replacements) {
      return Object.entries(replacements).reduce(
        (message, [token, value]) => message.split(token).join(String(value)),
        template || ''
      );
    }

    get cacheKey() {
      return `mostik:authors-index:v3:${this.locale}:${this.totalCount}`;
    }

    readCache() {
      try {
        const entries = JSON.parse(window.sessionStorage.getItem(this.cacheKey));
        return Array.isArray(entries) && entries.length === this.totalCount ? entries : null;
      } catch (error) {
        return null;
      }
    }

    writeCache(entries) {
      try {
        window.sessionStorage.setItem(this.cacheKey, JSON.stringify(entries));
      } catch (error) {
        // Search still works if storage is unavailable or full.
      }
    }
  }

  customElements.define('authors-directory', AuthorsDirectory);
}
